import React, { useCallback, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { useTranslation } from 'react-i18next';
import FilterActionWrapper from '../FilterActionWrapper';
import useOperationStepValues from 'router/data/useOperationStepValues';
import utils from 'utils';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button } from 'ncoded-component-library';
import useQueryFilter from 'hooks/useQueryFilter';
import { ListingQueryFilterParams } from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';
import BriefcaseIcon from 'icons/Briefcase.icon';

import './IndustryFilter.styles.scss';

type IndustryFilterProps = {
  className?: string;
  showIcon?: boolean;
};

export const INDUSTRY_FILTER_KEY = 'industry';

const IndustryFilter: React.FC<IndustryFilterProps> = (props) => {
  const { className, showIcon = true } = props;

  const { t } = useTranslation();
  const {
    params: { industry },
    onFiltersApply,
  } = useQueryFilter<ListingQueryFilterParams>();
  const { industrySelectOptions } = useOperationStepValues();
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => (industry ? utils.mapParamsToArray(industry) : []),
    [industry],
  );
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('industry-filter', className);

  const onSelectFilters = useCallback(
    (options: string[]) => {
      dropdown.setIsOpen(false);
      setButtonDisabled(true);

      const patchedVal = utils.multiSelectValueToStringArray(options as any);

      onFiltersApply(INDUSTRY_FILTER_KEY, patchedVal);
    },
    [dropdown, onFiltersApply],
  );

  const triggerTitle = useMemo(() => {
    return industry
      ? industry?.length < 2
        ? utils.stringifyFilterValues(industry)
        : t('openToSeeIndustryFilters')
      : t('industry');
  }, [industry, t]);

  useEffect(() => {
    setCurrentState(defaultVal);
  }, [defaultVal]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      onClose={() => {
        setCurrentState(defaultVal);
        setButtonDisabled(true);
      }}
      {...(showIcon && { icon: BriefcaseIcon })}
    >
      <ResolveMultiSelect
        className="res-industry-filter-dropdown"
        searchable
        noDataTitle={t('choseOption')}
        value={currentState}
        options={industrySelectOptions}
        onChange={(v) => {
          setButtonDisabled(false);
          setCurrentState(v);
        }}
        hideSelected={false}
      />

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={buttonDisabled}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyIndustryFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default IndustryFilter;
